/**
 * Main App Component
 *
 * @package
 */

import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { useConfiguratorState } from '../helpers/useConfiguratorState';
import { LoadingProductsContext } from '../helpers/LoadingProductsContext';
import TopBar from './TopBar';
import Sidebar from './Sidebar';
import ImageGallery from './ImageGallery';
import SummaryTable from './SummaryTable';
import PricingDisplayTabs from './PricingDisplayTabs';
import CTAButtons from './CTAButtons';
import BundleLoader from './BundleLoader';
import UrlParameterLoader from './UrlParameterLoader';
import DealerSection from './DealerSection';
import VehicleTitle from './VehicleTitle';
import { isNumericId } from '../helpers/getProductIdsFromSkus';

const App = ( { config } ) => {
	const urlParams = new URLSearchParams( window.location.search );
	const bundleId = urlParams.get( 'bundle' );
	const baseModelParam = urlParams.get( 'baseModel' );
	// True only until the URL base model is applied or turns out not to resolve. The
	// parameter stays in the URL afterwards, so deriving this on every render would keep
	// the gallery waiting for a base model that has already arrived, or been declined.
	const [ pendingBaseModel, setPendingBaseModel ] = useState( () => !! baseModelParam && ! bundleId && ( () => {
		if ( ! isNumericId( baseModelParam ) ) {
			return true;
		}
		const id = parseInt( baseModelParam, 10 );
		return ( config?.baseModels || [] ).some(
			m => ( typeof m === 'object' ? m.product_id : parseInt( m, 10 ) ) === id
		);
	} )() );

	// Share state with the qala-vat-switcher plugin via the 'vatinclexcl' cookie.
	// Cookie value: '1' = including VAT, '0' = excluding VAT.
	const getVatCookie = () => {
		const match = document.cookie.split( ';' ).find( c => c.trim().startsWith( 'vatinclexcl=' ) );
		return match ? match.split( '=' )[ 1 ].trim() : null;
	};

	const setVatCookie = value => {
		const expires = new Date();
		expires.setTime( expires.getTime() + ( 365 * 24 * 60 * 60 * 1000 ) );
		document.cookie = `vatinclexcl=${ value };expires=${ expires.toUTCString() };path=/`;
	};

	// Stay in sync with QuickCommerce (qcom), which persists the
	// choice in the 'qcom_price_display' cookie and mirrors it into the
	// 'qcUserPriceMode' localStorage key, both using 'including'/'excluding'.
	const QCOM_PRICE_COOKIE = 'qcom_price_display';
	const QCOM_PRICE_STORAGE_KEY = 'qcUserPriceMode';
	const QCOM_INCLUDING = 'including';
	const QCOM_EXCLUDING = 'excluding';

	const getQcomVatMode = () => {
		const match = document.cookie
			.split( ';' )
			.find( c => c.trim().startsWith( `${ QCOM_PRICE_COOKIE }=` ) );
		const fromCookie = match ? match.split( '=' )[ 1 ].trim() : '';
		if ( fromCookie === QCOM_INCLUDING || fromCookie === QCOM_EXCLUDING ) {
			return fromCookie;
		}

		try {
			const fromStorage = window.localStorage.getItem( QCOM_PRICE_STORAGE_KEY );
			if ( fromStorage === QCOM_INCLUDING || fromStorage === QCOM_EXCLUDING ) {
				return fromStorage;
			}
		} catch ( e ) {
			// localStorage unavailable (e.g. private mode) — fall through.
		}

		return null;
	};

	const setQcomVatMode = mode => {
		const value = mode === QCOM_INCLUDING ? QCOM_INCLUDING : QCOM_EXCLUDING;
		document.cookie = `${ QCOM_PRICE_COOKIE }=${ value };path=/;max-age=31536000;SameSite=Lax`;
		try {
			window.localStorage.setItem( QCOM_PRICE_STORAGE_KEY, value );
		} catch ( e ) {
			// localStorage unavailable — the cookie still carries the state.
		}
	};

	// For logged-in users, qcom resolves the mode from user meta (which outranks
	// the cookie), so persist there too. Fire-and-forget: the cookie/localStorage
	// still carry the state if this fails.
	const saveQcomVatModeToUserMeta = mode => {
		apiFetch( {
			path: '/at-configurator/v1/vat-mode',
			method: 'POST',
			data: { mode },
		} ).catch( () => {} );
	};

	const [ vatMode, setVatMode ] = useState( () => {
		if ( config?.isLoggedIn && ( config.vatMode === QCOM_INCLUDING || config.vatMode === QCOM_EXCLUDING ) ) {
			return config.vatMode;
		}

		const qcomMode = getQcomVatMode();
		if ( qcomMode ) {
			return qcomMode;
		}

		const cookie = getVatCookie();
		return cookie === '1' ? QCOM_INCLUDING : QCOM_EXCLUDING;
	} );

	const handleVatModeChange = mode => {
		setVatMode( mode );
		setVatCookie( mode === QCOM_INCLUDING ? '1' : '0' );
		setQcomVatMode( mode );

		if ( config?.isLoggedIn ) {
			saveQcomVatModeToUserMeta( mode );
		}
	};

	// Keep the configurator's VAT mode in step with the shared VAT switcher.
	// QuickCommerce fires the 'qcom:price-display-mode-changed' event (with the
	// new mode in event.detail.mode) whenever the switcher flips, so mirror that
	// straight into our state.
	useEffect( () => {
		const syncFromQcomSwitcher = event => {
			const mode = event?.detail?.mode;
			if ( mode === QCOM_INCLUDING || mode === QCOM_EXCLUDING ) {
				setVatMode( mode );
			}
		};

		window.addEventListener( 'qcom:price-display-mode-changed', syncFromQcomSwitcher );

		return () => window.removeEventListener( 'qcom:price-display-mode-changed', syncFromQcomSwitcher );
	}, [] );

	const {
		selectedBaseModel,
		setSelectedBaseModel,
		selectedProducts,
		setSelectedProducts,
		selectedBundle,
		setSelectedBundle,
		productQuantities,
		setProductQuantity,
		shareableId,
		expirationDate,
		getShareableUrl,
	} = useConfiguratorState( {
		baseModel: null,
		products: [],
		bundle: bundleId ? parseInt( bundleId, 10 ) : null,
	} );

	// Any base model choice, including "no base model" (0), ends the wait.
	useEffect( () => {
		if ( selectedBaseModel !== null ) {
			setPendingBaseModel( false );
		}
	}, [ selectedBaseModel ] );

	const workshopHourlyRate = config?.workshopHourlyRate || 0;
	const taxRate = config?.taxRate || 0;

	// A just-selected product's checkbox shows a spinner until the gallery preview
	// for that selection has loaded, then becomes the tick.
	const [ imageLoading, setImageLoading ] = useState( false );
	const [ loadingProductIds, setLoadingProductIds ] = useState( [] );
	const prevProductsRef = useRef( selectedProducts );
	const prevImageLoadingRef = useRef( false );
	const loadStartRef = useRef( 0 );
	const clearTimerRef = useRef( null );

	// Minimum spinner time so cached (instant) selections show a beat, not a flicker.
	const MIN_SPINNER_MS = 450;

	useEffect( () => {
		const added = selectedProducts.filter( id => ! prevProductsRef.current.includes( id ) );
		prevProductsRef.current = selectedProducts;
		if ( added.length > 0 ) {
			setLoadingProductIds( added );
		}
	}, [ selectedProducts ] );

	// Clear on the fetch's load-complete transition (adding a product always
	// fetches, so no stuck spinner), but not before MIN_SPINNER_MS has elapsed.
	useEffect( () => {
		if ( imageLoading && ! prevImageLoadingRef.current ) {
			loadStartRef.current = Date.now();
		}
		if ( prevImageLoadingRef.current && ! imageLoading ) {
			const remaining = Math.max( 0, MIN_SPINNER_MS - ( Date.now() - loadStartRef.current ) );
			clearTimerRef.current = setTimeout( () => {
				setLoadingProductIds( prev => ( prev.length > 0 ? [] : prev ) );
			}, remaining );
		}
		prevImageLoadingRef.current = imageLoading;

		return () => clearTimeout( clearTimerRef.current );
	}, [ imageLoading ] );

	return (
		<LoadingProductsContext.Provider value={ loadingProductIds }>
			<div className="configurator-page">
				{ /* Skip links for keyboard navigation */ }
				<a href="#configurator-main-content" className="configurator-skip-link">
					{ __( 'Skip to main content', 'at-vehicle-configurator' ) }
				</a>
				<a href="#configurator-summary-section" className="configurator-skip-link">
					{ __( 'Skip to configuration summary', 'at-vehicle-configurator' ) }
				</a>
				{ /* Screen reader announcements */ }
				<div
					role="status"
					aria-live="polite"
					aria-atomic="true"
					className="screen-reader-text"
					id="configurator-announcements"
				/>
				{ bundleId && (
					<BundleLoader
						bundleId={ parseInt( bundleId, 10 ) }
						setSelectedBaseModel={ setSelectedBaseModel }
						setSelectedProducts={ setSelectedProducts }
						setSelectedBundle={ setSelectedBundle }
					/>
				) }
				{ baseModelParam && (
					<UrlParameterLoader
						productIds={ null }
						baseModelId={ baseModelParam }
						baseModels={ config?.baseModels || [] }
						vehicleConfigId={ config?.vehicleConfigId }
						hasBundle={ !! bundleId }
						setSelectedBaseModel={ setSelectedBaseModel }
						setSelectedProducts={ setSelectedProducts }
						onBaseModelUnresolved={ () => setPendingBaseModel( false ) }
					/>
				) }
				<TopBar
					config={ config }
					selectedBaseModel={ selectedBaseModel }
					selectedProducts={ selectedProducts }
					vatMode={ vatMode }
					setVatMode={ handleVatModeChange }
					workshopHourlyRate={ workshopHourlyRate }
					taxRate={ taxRate }
					quantities={ productQuantities }
				/>
				<main role="main" id="configurator-main-content" className="configurator-container">
					<ImageGallery
						config={ config }
						selectedBaseModel={ selectedBaseModel }
						selectedProducts={ selectedProducts }
						pendingBaseModel={ pendingBaseModel }
						onLoadingChange={ setImageLoading }
					/>
					<Sidebar
						config={ config }
						selectedBaseModel={ selectedBaseModel }
						setSelectedBaseModel={ setSelectedBaseModel }
						selectedProducts={ selectedProducts }
						setSelectedProducts={ setSelectedProducts }
						selectedBundle={ selectedBundle }
						setSelectedBundle={ setSelectedBundle }
						vatMode={ vatMode }
						quantities={ productQuantities }
						setProductQuantity={ setProductQuantity }
						workshopHourlyRate={ workshopHourlyRate }
						taxRate={ taxRate }
					/>
				</main>
				<section id="configurator-summary-section" aria-label={ __( 'Configuration Summary', 'at-vehicle-configurator' ) }>
					<div className="configurator-summary-header">
						<div className="configurator-summary-header-label">{ __( 'Your Arctic Truck Configuration', 'at-vehicle-configurator' ) }</div>
						<h2 className="configurator-summary-title">{ __( 'Summary', 'at-vehicle-configurator' ) }</h2>
					</div>
					<div className="configurator-summary-container">
						<SummaryTable
							config={ config }
							selectedBaseModel={ selectedBaseModel }
							selectedProducts={ selectedProducts }
							selectedBundle={ selectedBundle }
							vatMode={ vatMode }
							quantities={ productQuantities }
							setProductQuantity={ setProductQuantity }
							workshopHourlyRate={ workshopHourlyRate }
							taxRate={ taxRate }
						/>
						<aside className="configurator-summary-sidebar" aria-label={ __( 'Price Summary and Actions', 'at-vehicle-configurator' ) }>
							<div className="configurator-summary-sidebar-header">
								<div className="configurator-summary-sidebar-label">{ __( 'Total', 'at-vehicle-configurator' ) }</div>
								<VehicleTitle
									vehicleConfigId={ config?.vehicleConfigId }
									className="configurator-vehicle-title"
								/>
							</div>
							<div className="configurator-summary-sidebar-content">
								<PricingDisplayTabs
									selectedBaseModel={ selectedBaseModel }
									selectedProducts={ selectedProducts }
									financingConfig={ config?.acf || {} }
									vatMode={ vatMode }
									onVatToggle={ handleVatModeChange }
									workshopHourlyRate={ workshopHourlyRate }
									taxRate={ taxRate }
									quantities={ productQuantities }
								/>
								<CTAButtons
									shareableCode={ shareableId }
									expirationDate={ expirationDate }
									selectedBaseModel={ selectedBaseModel }
									selectedProducts={ selectedProducts }
									getShareableUrl={ getShareableUrl }
									vehicleConfigId={ config?.vehicleConfigId }
									hideDealersSection={ config?.hideDealersSection ?? false }
									vatMode={ vatMode }
									quantities={ productQuantities }
								/>
							</div>
						</aside>
					</div>
				</section>
				{ ! ( config?.hideDealersSection ) && (
					<DealerSection
						vehicleBrand={ config.brand }
						selectedBaseModel={ selectedBaseModel }
						selectedProducts={ selectedProducts }
						vehicleConfigId={ config?.vehicleConfigId }
						shareableCode={ shareableId }
						vatMode={ vatMode }
						quantities={ productQuantities }
					/>
				) }
			</div>
		</LoadingProductsContext.Provider>
	);
};

App.propTypes = {
	config: PropTypes.shape( {
		vehicleConfigId: PropTypes.number,
		brand: PropTypes.string,
		baseModels: PropTypes.array,
		productGroups: PropTypes.array,
		sectionSettings: PropTypes.object,
		initialImage: PropTypes.string,
		hideDealersSection: PropTypes.bool,
		contactAdminEmail: PropTypes.string,
	} ).isRequired,
};

export default App;
